import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { AuthProvider } from './contexts/AuthContext';
import { UIProvider } from './contexts/UIContext';
import Navigation from './components/Navigation';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import ForgotPasswordForm from './components/auth/ForgotPasswordForm';
import Dashboard from './pages/Dashboard';
import Wardrobe from './pages/Wardrobe';
import Upload from './pages/Upload';
import FitOfTheDay from './pages/FitOfTheDay';
import Outfits from './pages/Outfits';
import OutfitRecommendations from './pages/OutfitRecommendations';
import Collections from './pages/Collections';
import LookBook from './pages/LookBook';
import Calendar from './pages/Calendar';
import TripPlanner from './pages/TripPlanner';
import ProfilePage from './pages/ProfilePage';
import FriendsPage from './pages/FriendsPage';
import ActivityFeedPage from './pages/ActivityFeedPage';
import SearchPage from './pages/SearchPage';
import ConversationsPage from './pages/ConversationsPage';
import FloatingActions from './components/FloatingActions';
import RouteTransition from './components/RouteTransition';
import ErrorBoundary from './components/ErrorBoundary';
import fashionTheme from './theme/fashionTheme';
import './styles/animations.css';

const ProtectedShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return (
    <ProtectedRoute>
      <>
        <Navigation />
        {/* Per-page boundary keyed by path: a crash in one page keeps the nav
            usable and clears automatically when the user navigates elsewhere.
            Bottom padding on mobile clears the fixed bottom tab bar + safe area. */}
        <Box sx={{ pb: { xs: 'calc(56px + env(safe-area-inset-bottom))', md: 0 } }}>
          <ErrorBoundary key={location.pathname} context={location.pathname}>
            <RouteTransition>{children}</RouteTransition>
          </ErrorBoundary>
        </Box>
        <FloatingActions />
      </>
    </ProtectedRoute>
  );
};

const AnimatedRoutes: React.FC = () => {
  // useLocation triggers RouteTransition's keyed remount via children path
  useLocation();
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginForm />} />
      <Route path="/register" element={<RegisterForm />} />
      <Route path="/forgot-password" element={<ForgotPasswordForm />} />

      {/* Protected routes */}
      <Route path="/" element={<ProtectedShell><Dashboard /></ProtectedShell>} />
      <Route path="/wardrobe" element={<ProtectedShell><Wardrobe /></ProtectedShell>} />
      <Route path="/upload" element={<ProtectedShell><Upload /></ProtectedShell>} />
      <Route path="/fit-of-the-day" element={<ProtectedShell><FitOfTheDay /></ProtectedShell>} />
      <Route path="/outfits" element={<ProtectedShell><Outfits /></ProtectedShell>} />
      <Route path="/collections" element={<ProtectedShell><Collections /></ProtectedShell>} />
      <Route path="/look-book" element={<ProtectedShell><LookBook /></ProtectedShell>} />
      <Route path="/look-book/:lookId" element={<ProtectedShell><LookBook /></ProtectedShell>} />
      <Route path="/calendar" element={<ProtectedShell><Calendar /></ProtectedShell>} />
      <Route path="/trip-planner" element={<ProtectedShell><TripPlanner /></ProtectedShell>} />
      {/* Search and Recommendations are consolidated into the Assistant (chat) */}
      <Route path="/recommendations" element={<Navigate to="/conversations" replace />} />
      <Route path="/profile" element={<ProtectedShell><ProfilePage /></ProtectedShell>} />
      <Route path="/profile/:userId" element={<ProtectedShell><ProfilePage /></ProtectedShell>} />
      <Route path="/friends" element={<ProtectedShell><FriendsPage /></ProtectedShell>} />
      <Route path="/activity" element={<ProtectedShell><ActivityFeedPage /></ProtectedShell>} />
      <Route path="/search" element={<Navigate to="/conversations" replace />} />
      <Route path="/conversations" element={<ProtectedShell><ConversationsPage /></ProtectedShell>} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={fashionTheme}>
      <CssBaseline />
      <UIProvider>
        <AuthProvider>
          <Router>
            <ErrorBoundary context="app-root">
              <AnimatedRoutes />
            </ErrorBoundary>
          </Router>
        </AuthProvider>
      </UIProvider>
    </ThemeProvider>
  );
}

export default App;
